import { yupResolver } from '@hookform/resolvers/yup'
import { find, map } from 'lodash-es'
import { ChangeEvent, useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import Loader from '../../../../common/components/Loader'
import {
  EmailTemplateFooter,
  EmailTemplateFooterPayload,
  Languages,
  TemplateTranslations,
  Variable,
} from '../../../../common/models'
import FormGroup from '../../../../core/components/FormGroup'
import { FormUploadPreview } from '../../../../core/components/FormUploadPreview'
import { HtmlEditor } from '../../../../core/components/HtmlEditor'
import { useCheckPermission } from '../../../../core/hooks/use-check-permission'
import {
  getEmailTemplateFooter,
  updateEmailTemplateFooter,
} from '../../../../core/services/email-templates'
import {
  uploadImage,
  uploadNonPublicImage,
} from '../../../../core/services/upload-image'
import { MessageBox } from '../../messages/MessageBox'
import { Button, Card, Checkbox, Input } from '@/components/ui'
import { AlertRatio } from '@/components/AlertRatio'

interface IFooterValues {
  logoEnabled: boolean
  logoLink?: string | null
  socialMediaEnabled: boolean
  instagramEnabled?: boolean
  instagramUrl?: string | null
  instagramLogoLink?: string | null
  youtubeEnabled?: boolean
  youtubeUrl?: string | null
  youtubeLogoLink?: string | null
  facebookEnabled?: boolean
  facebookUrl?: string | null
  facebookLogoLink?: string | null
  tiktokEnabled?: boolean
  tiktokUrl?: string | null
  tiktokLogoLink?: string | null
  twitterEnabled?: boolean
  twitterUrl?: string | null
  twitterLogoLink?: string | null
  telegramEnabled?: boolean
  telegramUrl?: string | null
  telegramLogoLink?: string | null
  whatsappEnabled?: boolean
  whatsappUrl?: string | null
  whatsappLogoLink?: string | null
  descriptionEnabled: boolean
  descriptionTexts?: TemplateTranslations[]
  copyrightEnabled: boolean
  copyrightTexts?: TemplateTranslations[]
}

interface IImageValues {
  instagramLink?: string | null
  instagramName?: string | null
  youtubeLink?: string | null
  youtubeName?: string | null
  facebookLink?: string | null
  facebookName?: string | null
  tiktokLink?: string | null
  tiktokName?: string | null
  twitterLink?: string | null
  twitterName?: string | null
  telegramLink?: string | null
  telegramName?: string | null
  whatsappLink?: string | null
  whatsappName?: string | null
}

interface FooterProps {
  variables: Variable[]
  allLanguages: Languages[]
}

export const Footer = (props: FooterProps) => {
  const { variables, allLanguages } = props
  const { editionId, festivalName } = useParams()
  const { hasEditPermission } = useCheckPermission({ page: 'SETTINGS' })
  const [items, setItems] = useState<IImageValues>({})
  const [footerCancel, setFooterCancel] = useState<EmailTemplateFooter>()
  const [isErrorRatio, setIsErrorRatio] = useState<boolean>(false)
  const [invalidPhoto, setInvalidPhoto] = useState<boolean>(false)
  const [image, setImage] = useState('')
  const [imageName, setImageName] = useState('')
  const [isUploading, setIsUploading] = useState<boolean>(true)

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const getNameFromUrl = (imageUrl: string) => {
    if (!imageUrl) return ''
    const url = new URL(imageUrl)
    const i = url?.pathname?.lastIndexOf('/') + 1
    const imageName = url?.pathname?.substring(i)
    return imageName
  }

  useEffect(() => {
    // prettier-ignore
    (async () => {
      setIsUploading(true)
      try {
        const resp = await getEmailTemplateFooter()
        if (resp.status === 200) {
          setFooterCancel(resp.data)
          setImage(resp.data.logoLink)
          setImageName(getNameFromUrl(resp.data.logoLink))
          setItems((i) => ({
            ...i,
            instagramLink: resp.data.instagramLogoLink,
            instagramName: getNameFromUrl(resp.data.instagramLogoLink),
            youtubeLink: resp.data.youtubeLogoLink,
            youtubeName: getNameFromUrl(resp.data.youtubeLogoLink),
            facebookLink: resp.data.facebookLogoLink,
            facebookName: getNameFromUrl(resp.data.facebookLogoLink),
            tiktokLink: resp.data.tiktokLogoLink,
            tiktokName: getNameFromUrl(resp.data.tiktokLogoLink),
            twitterLink: resp.data.twitterLogoLink,
            twitterName: getNameFromUrl(resp.data.twitterLogoLink),
            telegramLink: resp.data.telegramLogoLink,
            telegramName: getNameFromUrl(resp.data.telegramLogoLink),
            whatsappLink: resp.data.whatsappLogoLink,
            whatsappName: getNameFromUrl(resp.data.whatsappLogoLink),
          }))

          setValue('logoLink', resp.data.logoLink)
          setValue('logoEnabled', resp.data.logoEnabled)
          setValue('socialMediaEnabled', resp.data.socialMediaEnabled)
          setValue('instagramEnabled', resp.data.instagramEnabled)
          setValue('instagramUrl', resp.data.instagramUrl)
          setValue('instagramLogoLink', resp.data.instagramLogoLink)
          setValue('youtubeEnabled', resp.data.youtubeEnabled)
          setValue('youtubeUrl', resp.data.youtubeUrl)
          setValue('youtubeLogoLink', resp.data.youtubeLogoLink)
          setValue('facebookEnabled', resp.data.facebookEnabled)
          setValue('facebookUrl', resp.data.facebookUrl)
          setValue('facebookLogoLink', resp.data.facebookLogoLink)
          setValue('tiktokEnabled', resp.data.tiktokEnabled)
          setValue('tiktokUrl', resp.data.tiktokUrl)
          setValue('tiktokLogoLink', resp.data.tiktokLogoLink)
          setValue('twitterEnabled', resp.data.twitterEnabled)
          setValue('twitterUrl', resp.data.twitterUrl)
          setValue('twitterLogoLink', resp.data.twitterLogoLink)
          setValue('telegramEnabled', resp.data.telegramEnabled)
          setValue('telegramUrl', resp.data.telegramUrl)
          setValue('telegramLogoLink', resp.data.telegramLogoLink)
          setValue('whatsappEnabled', resp.data.whatsappEnabled)
          setValue('whatsappUrl', resp.data.whatsappUrl)
          setValue('whatsappLogoLink', resp.data.whatsappLogoLink)

          setValue('descriptionTexts', resp.data.descriptionTexts)
          setValue('descriptionEnabled', resp.data.descriptionEnabled)

          setValue('copyrightTexts', resp.data.copyrightTexts)
          setValue('copyrightEnabled', resp.data.copyrightEnabled)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setIsUploading(false)
      }
    })()
  }, [editionId, festivalName])

  const schema = yup.object().shape(
    {
      logoEnabled: yup.boolean().required('Field required!'),
      logoLink: yup.string().when('logoEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional(),
      }),
      socialMediaEnabled: yup.boolean().required('Field required'),
      instagramEnabled: yup.boolean().when('socialMediaEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional(),
      }),
      instagramUrl: yup.string().when('instagramEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      instagramLogoLink: yup.string().when('instagramEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      youtubeEnabled: yup.boolean().when('socialMediaEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional(),
      }),
      youtubeUrl: yup.string().when('youtubeEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      youtubeLogoLink: yup.string().when('youtubeEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      facebookEnabled: yup.boolean().when('socialMediaEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional(),
      }),
      facebookUrl: yup.string().when('facebookEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      facebookLogoLink: yup.string().when('facebookEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      tiktokEnabled: yup.boolean().when('socialMediaEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional(),
      }),
      tiktokUrl: yup.string().when('tiktokEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      tiktokLogoLink: yup.string().when('tiktokEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      twitterEnabled: yup.boolean().when('socialMediaEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional(),
      }),
      twitterUrl: yup.string().when('twitterEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      twitterLogoLink: yup.string().when('twitterEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      telegramEnabled: yup.boolean().when('socialMediaEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional(),
      }),
      telegramUrl: yup.string().when('telegramEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      telegramLogoLink: yup.string().when('telegramEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      whatsappEnabled: yup.boolean().when('socialMediaEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional(),
      }),
      whatsappUrl: yup.string().when('whatsappEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      whatsappLogoLink: yup.string().when('whatsappEnabled', {
        is: (val: boolean) => (val ? true : false),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      descriptionEnabled: yup.boolean().required('Field is required'),
      descriptionTexts: yup
        .array()
        .of(
          yup.object().shape({
            id: yup.string().required('Field is required'),
            locale: yup.string().required('Field is required'),
            text: yup.string().required('Field is required'),
          }),
        )
        .when('descriptionEnabled', {
          is: (val: boolean) => (val ? true : false),
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.optional().nullable(),
        }),
      copyrightEnabled: yup.boolean().required('Field is required'),
      copyrightTexts: yup
        .array()
        .of(
          yup.object().shape({
            id: yup.string().required('Field is required'),
            locale: yup.string().required('Field is required'),
            text: yup.string().required('Field is required'),
          }),
        )
        .when('copyrightEnabled', {
          is: (val: boolean) => (val ? true : false),
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.optional().nullable(),
        }),
    },
    [
      ['socialMediaEnabled', 'instagramEnabled'],
      ['instagramEnabled', 'instagramSVG'],
      ['instagramEnabled', 'instagramURL'],
      ['socialMediaEnabled', 'youtubeEnabled'],
      ['youtubeEnabled', 'youtubeSVG'],
      ['youtubeEnabled', 'youtubeURL'],
      ['socialMediaEnabled', 'facebookEnabled'],
      ['facebookEnabled', 'facebookSVG'],
      ['facebookEnabled', 'facebookURL'],
      ['socialMediaEnabled', 'tiktokEnabled'],
      ['tiktokEnabled', 'tiktokSVG'],
      ['tiktokEnabled', 'tiktokURL'],
      ['socialMediaEnabled', 'twitterEnabled'],
      ['twitterEnabled', 'twitterSVG'],
      ['twitterEnabled', 'twitterURL'],
      ['socialMediaEnabled', 'telegramEnabled'],
      ['telegramEnabled', 'telegramSVG'],
      ['telegramEnabled', 'telegramURL'],
      ['socialMediaEnabled', 'whatsappEnabled'],
      ['whatsappEnabled', 'whatsappSVG'],
      ['whatsappEnabled', 'whatsappURL'],
    ],
  )

  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    watch,
    control,
  } = useForm<IFooterValues>({
    mode: 'onChange',
    resolver: yupResolver<IFooterValues>(schema),
  })

  const { fields: fieldsDescription } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'descriptionTexts', // unique name for your Field Array
  })

  const { fields: fieldsCopyright } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'copyrightTexts', // unique name for your Field Array
  })

  const copyWatch = watch('copyrightEnabled')
  const descWatch = watch('descriptionEnabled')
  const mediaWatch = watch('socialMediaEnabled')
  const logoWatch = watch('logoEnabled')

  const instagramEnabledWatch = watch('instagramEnabled')
  const youtubeEnabledWatch = watch('youtubeEnabled')
  const facebookEnabledWatch = watch('facebookEnabled')
  const tiktokEnabledWatch = watch('tiktokEnabled')
  const twitterEnabledWatch = watch('twitterEnabled')
  const telegramEnabledWatch = watch('telegramEnabled')
  const whatsappEnabledWatch = watch('whatsappEnabled')

  const submit = async (values: IFooterValues) => {
    setIsUploading(true)
    const payload: EmailTemplateFooterPayload = {
      logoEnabled: values.logoEnabled,
      socialMediaEnabled: values.socialMediaEnabled,
      instagramEnabled: values.instagramEnabled,
      youtubeEnabled: values.youtubeEnabled,
      facebookEnabled: values.facebookEnabled,
      tiktokEnabled: values.tiktokEnabled,
      twitterEnabled: values.twitterEnabled,
      telegramEnabled: values.telegramEnabled,
      whatsappEnabled: values.whatsappEnabled,
      descriptionEnabled: values.descriptionEnabled,
      copyrightEnabled: values.copyrightEnabled,
    }
    if (values.logoEnabled) {
      payload.logoKey = values.logoLink !== image ? values.logoLink : null
    }
    if (values.instagramEnabled) {
      payload.instagramLogoKey =
        values.instagramLogoLink !== items.instagramLink
          ? values.instagramLogoLink
          : null
      payload.instagramUrl = values.instagramUrl
    }
    if (values.youtubeEnabled) {
      payload.youtubeLogoKey =
        values.youtubeLogoLink !== items.youtubeLink
          ? values.youtubeLogoLink
          : null
      payload.youtubeUrl = values.youtubeUrl
    }
    if (values.facebookEnabled) {
      payload.facebookLogoKey =
        values.facebookLogoLink !== items.facebookLink
          ? values.facebookLogoLink
          : null
      payload.facebookUrl = values.facebookUrl
    }
    if (values.tiktokEnabled) {
      payload.tiktokLogoKey =
        values.tiktokLogoLink !== items.tiktokLink
          ? values.tiktokLogoLink
          : null
      payload.tiktokUrl = values.tiktokUrl
    }
    if (values.twitterEnabled) {
      payload.twitterLogoKey =
        values.twitterLogoLink !== items.twitterLink
          ? values.twitterLogoLink
          : null
      payload.twitterUrl = values.twitterUrl
    }
    if (values.telegramEnabled) {
      payload.telegramLogoKey =
        values.telegramLogoLink !== items.telegramLink
          ? values.telegramLogoLink
          : null
      payload.telegramUrl = values.telegramUrl
    }
    if (values.whatsappEnabled) {
      payload.whatsappLogoKey =
        values.whatsappLogoLink !== items.whatsappLink
          ? values.whatsappLogoLink
          : null
      payload.whatsappUrl = values.whatsappUrl
    }
    if (values.descriptionEnabled) {
      payload.descriptionTexts = values.descriptionTexts
    }
    if (values.copyrightEnabled) {
      payload.copyrightTexts = values.copyrightTexts
    }
    try {
      const resp = await updateEmailTemplateFooter(payload)
      if (resp.status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setIsUploading(false)
    }
  }

  const handleCancel = async () => {
    if (footerCancel) {
      setValue('logoLink', footerCancel.logoLink)
      setValue('logoEnabled', footerCancel.logoEnabled)
      setValue('socialMediaEnabled', footerCancel.socialMediaEnabled)
      setValue('instagramEnabled', footerCancel.instagramEnabled)
      setValue('instagramUrl', footerCancel.instagramUrl)
      setValue('instagramLogoLink', footerCancel.instagramLogoLink)
      setValue('youtubeEnabled', footerCancel.youtubeEnabled)
      setValue('youtubeUrl', footerCancel.youtubeUrl)
      setValue('youtubeLogoLink', footerCancel.youtubeLogoLink)
      setValue('facebookEnabled', footerCancel.facebookEnabled)
      setValue('facebookUrl', footerCancel.facebookUrl)
      setValue('facebookLogoLink', footerCancel.facebookLogoLink)
      setValue('tiktokEnabled', footerCancel.tiktokEnabled)
      setValue('tiktokUrl', footerCancel.tiktokUrl)
      setValue('tiktokLogoLink', footerCancel.tiktokLogoLink)
      setValue('twitterEnabled', footerCancel.twitterEnabled)
      setValue('twitterUrl', footerCancel.twitterUrl)
      setValue('twitterLogoLink', footerCancel.twitterLogoLink)
      setValue('telegramEnabled', footerCancel.telegramEnabled)
      setValue('telegramUrl', footerCancel.telegramUrl)
      setValue('telegramLogoLink', footerCancel.telegramLogoLink)
      setValue('whatsappEnabled', footerCancel.whatsappEnabled)
      setValue('whatsappUrl', footerCancel.whatsappUrl)
      setValue('whatsappLogoLink', footerCancel.whatsappLogoLink)

      setValue('descriptionTexts', footerCancel.descriptionTexts)
      setValue('descriptionEnabled', footerCancel.descriptionEnabled)

      setValue('copyrightTexts', footerCancel.copyrightTexts)
      setValue('copyrightEnabled', footerCancel.copyrightEnabled)
    }
  }

  if (isUploading) {
    return <Loader />
  }

  return (
    <Card className="p-[30px] max-h-[calc(100vh-215px)] bg-gradient-4 overflow-y-auto">
      <form
        id="settings-email-templates-footer-form"
        onSubmit={handleSubmit(submit)}
        className="space-y-7"
      >
        <div className="flex flex-col gap-5">
          <div className="bg-monochrome-3-60 border border-monochrome-120 p-4 rounded-2xl space-y-5">
            <FormGroup
              label="Logo"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{
                htmlFor: 'logoEnabled',
              }}
            >
              <Checkbox
                id="logoEnabled"
                checked={watch('logoEnabled')}
                onCheckedChange={(checked) => {
                  setValue('logoEnabled', Boolean(checked))
                }}
                disabled={!hasEditPermission}
              />
            </FormGroup>
            {logoWatch && (
              <>
                <FormUploadPreview
                  control={
                    <Input
                      multiple={false}
                      accept="image/*"
                      isInvalid={
                        !!errors?.logoLink?.message ||
                        invalidPhoto ||
                        isErrorRatio
                      }
                      disabled={!hasEditPermission}
                      name="logoLink"
                      onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                        const file = e?.target?.files?.[0]
                        if (file) {
                          if (file.size > 1_000_000) {
                            setInvalidPhoto(true)
                            setIsError(true)
                            setShowMessageBox(true)
                            setMessage('The photo is too large (1 MB)')
                            return
                          }
                          const data = await uploadImage(
                            file,
                            setIsUploading,
                            setIsError,
                            setShowMessageBox,
                            setMessage,
                            setIsErrorRatio,
                            'NaN',
                            false,
                          )
                          if (data.photoUrl) {
                            setImage(data.photoUrl)
                            setImageName(file.name)
                            setValue('logoLink', data.photoUrl, {
                              shouldValidate: true,
                              shouldDirty: true,
                            })
                          }
                        }
                      }}
                      type="file"
                    />
                  }
                  disabled={!hasEditPermission}
                  imageName={imageName}
                  isUploading={isUploading}
                  url={image}
                  onRemove={() => {
                    setImage('')
                    setImageName('')
                    setValue('logoLink', '')
                  }}
                  imageStyle={{
                    objectFit: 'contain',
                  }}
                  label="Logo Upload PNG"
                  previewLabel="Logo Upload PNG"
                />
                {isErrorRatio && <AlertRatio />}
              </>
            )}
          </div>
          <div className="bg-monochrome-3-60 border border-monochrome-120 p-4 rounded-2xl space-y-5">
            <FormGroup
              label="Social Media"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{
                htmlFor: 'socialMediaEnabled',
              }}
            >
              <Checkbox
                id="socialMediaEnabled"
                checked={watch('socialMediaEnabled')}
                onCheckedChange={(checked) => {
                  setValue('socialMediaEnabled', Boolean(checked))
                }}
                disabled={!hasEditPermission}
              />
            </FormGroup>
            {!!mediaWatch && (
              <div className="flex flex-col gap-4 w-full">
                <div className="bg-monochrome-3-60 border border-monochrome-120 p-4 rounded-2xl space-y-5">
                  <FormGroup
                    label="Instagram"
                    direction="row"
                    labelClassName="whitespace-nowrap"
                    inputWrapperClassName="w-auto"
                    wrapperClassName="w-auto flex-row-reverse justify-end"
                    labelProps={{
                      htmlFor: 'instagramEnabled',
                    }}
                  >
                    <Checkbox
                      id="instagramEnabled"
                      checked={watch('instagramEnabled')}
                      onCheckedChange={(checked) => {
                        setValue('instagramEnabled', Boolean(checked))
                      }}
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  {!!instagramEnabledWatch && (
                    <>
                      <FormUploadPreview
                        disabled={!hasEditPermission}
                        control={
                          <Input
                            multiple={false}
                            accept="image/png"
                            isInvalid={
                              !!errors?.instagramLogoLink?.message ||
                              invalidPhoto ||
                              isErrorRatio
                            }
                            disabled={!hasEditPermission}
                            name="instagramLogoLink"
                            onChange={async (
                              e: ChangeEvent<HTMLInputElement>,
                            ) => {
                              const file = e?.target?.files?.[0]
                              if (file) {
                                const data = await uploadImage(
                                  file,
                                  setIsUploading,
                                  setIsError,
                                  setShowMessageBox,
                                  setMessage,
                                  setIsErrorRatio,
                                  'NaN',
                                  false,
                                )
                                if (data.photoUrl) {
                                  setItems((i) => ({
                                    ...i,
                                    instagramLink: data.photoUrl,
                                    instagramName: file.name,
                                  }))
                                  setValue('instagramLogoLink', data.photoKey, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  })
                                }
                              }
                            }}
                            type="file"
                          />
                        }
                        imageName={items.instagramName || ''}
                        isUploading={isUploading}
                        url={items.instagramLink || ''}
                        onRemove={() => {
                          setItems((i) => ({
                            ...i,
                            instagramLink: '',
                            instagramName: '',
                          }))
                          setValue('instagramLogoLink', '')
                        }}
                        imageStyle={{
                          objectFit: 'contain',
                        }}
                        label="Instagram Icon PNG"
                        previewLabel="Instagram Icon PNG"
                      />
                      <FormGroup label="Instagram">
                        <Input
                          isInvalid={!!errors?.instagramUrl?.message}
                          {...register('instagramUrl')}
                          type="text"
                          placeholder="Enter URL"
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                    </>
                  )}
                </div>
                <div className="bg-monochrome-3-60 border border-monochrome-120 p-4 rounded-2xl space-y-5">
                  <FormGroup
                    label="Youtube"
                    direction="row"
                    labelClassName="whitespace-nowrap"
                    inputWrapperClassName="w-auto"
                    wrapperClassName="w-auto flex-row-reverse justify-end"
                    labelProps={{
                      htmlFor: 'youtubeEnabled',
                    }}
                  >
                    <Checkbox
                      id="youtubeEnabled"
                      checked={watch('youtubeEnabled')}
                      onCheckedChange={(checked) => {
                        setValue('youtubeEnabled', Boolean(checked))
                      }}
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  {!!youtubeEnabledWatch && (
                    <>
                      <FormUploadPreview
                        disabled={!hasEditPermission}
                        control={
                          <Input
                            multiple={false}
                            accept="image/png"
                            isInvalid={
                              !!errors?.youtubeLogoLink?.message ||
                              invalidPhoto ||
                              isErrorRatio
                            }
                            name="youtubeLogoLink"
                            disabled={!hasEditPermission}
                            onChange={async (
                              e: ChangeEvent<HTMLInputElement>,
                            ) => {
                              const file = e?.target?.files?.[0]
                              if (file) {
                                const data = await uploadImage(
                                  file,
                                  setIsUploading,
                                  setIsError,
                                  setShowMessageBox,
                                  setMessage,
                                  setIsErrorRatio,
                                  'NaN',
                                  false,
                                )
                                if (data.photoUrl) {
                                  setItems((i) => ({
                                    ...i,
                                    youtubeLink: data.photoUrl,
                                    youtubeName: file.name,
                                  }))
                                  setValue('youtubeLogoLink', data.photoKey, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  })
                                }
                              }
                            }}
                            type="file"
                          />
                        }
                        imageName={items.youtubeName || ''}
                        isUploading={isUploading}
                        url={items.youtubeLink || ''}
                        onRemove={() => {
                          setItems((i) => ({
                            ...i,
                            youtubeLink: '',
                            youtubeName: '',
                          }))
                          setValue('youtubeLogoLink', '')
                        }}
                        imageStyle={{
                          objectFit: 'contain',
                        }}
                        label="Youtube Icon PNG"
                        previewLabel="Youtube Icon PNG"
                      />
                      <FormGroup label="Youtube">
                        <Input
                          isInvalid={!!errors?.youtubeUrl?.message}
                          {...register('youtubeUrl')}
                          type="text"
                          placeholder="Enter URL"
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                    </>
                  )}
                </div>
                <div className="bg-monochrome-3-60 border border-monochrome-120 p-4 rounded-2xl space-y-5">
                  <FormGroup
                    label="Facebook"
                    direction="row"
                    labelClassName="whitespace-nowrap"
                    inputWrapperClassName="w-auto"
                    wrapperClassName="w-auto flex-row-reverse justify-end"
                    labelProps={{
                      htmlFor: 'facebookEnabled',
                    }}
                  >
                    <Checkbox
                      id="facebookEnabled"
                      checked={watch('facebookEnabled')}
                      onCheckedChange={(checked) => {
                        setValue('facebookEnabled', Boolean(checked))
                      }}
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  {!!facebookEnabledWatch && (
                    <>
                      <FormUploadPreview
                        disabled={!hasEditPermission}
                        control={
                          <Input
                            multiple={false}
                            accept="image/png"
                            isInvalid={
                              !!errors?.facebookLogoLink?.message ||
                              invalidPhoto ||
                              isErrorRatio
                            }
                            name="facebookLogoLink"
                            disabled={!hasEditPermission}
                            onChange={async (
                              e: ChangeEvent<HTMLInputElement>,
                            ) => {
                              const file = e?.target?.files?.[0]
                              if (file) {
                                const data = await uploadImage(
                                  file,
                                  setIsUploading,
                                  setIsError,
                                  setShowMessageBox,
                                  setMessage,
                                  setIsErrorRatio,
                                  'NaN',
                                  false,
                                )
                                if (data.photoUrl) {
                                  setItems((i) => ({
                                    ...i,
                                    facebookLink: data.photoUrl,
                                    facebookName: file.name,
                                  }))
                                  setValue('facebookLogoLink', data.photoKey, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  })
                                }
                              }
                            }}
                            type="file"
                          />
                        }
                        imageName={items.facebookName || ''}
                        isUploading={isUploading}
                        url={items.facebookLink || ''}
                        onRemove={() => {
                          setItems((i) => ({
                            ...i,
                            facebookLink: '',
                            facebookName: '',
                          }))
                          setValue('facebookLogoLink', '')
                        }}
                        imageStyle={{
                          objectFit: 'contain',
                        }}
                        label="Facebook Icon PNG"
                        previewLabel="Facebook Icon PNG"
                      />
                      <FormGroup label="Facebook">
                        <Input
                          isInvalid={!!errors?.facebookUrl?.message}
                          {...register('facebookUrl')}
                          type="text"
                          placeholder="Enter URL"
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                    </>
                  )}
                </div>
                <div className="bg-monochrome-3-60 border border-monochrome-120 p-4 rounded-2xl space-y-5">
                  <FormGroup
                    label="TikTok"
                    direction="row"
                    labelClassName="whitespace-nowrap"
                    inputWrapperClassName="w-auto"
                    wrapperClassName="w-auto flex-row-reverse justify-end"
                    labelProps={{
                      htmlFor: 'tiktokEnabled',
                    }}
                  >
                    <Checkbox
                      id="tiktokEnabled"
                      checked={watch('tiktokEnabled')}
                      onCheckedChange={(checked) => {
                        setValue('tiktokEnabled', Boolean(checked))
                      }}
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  {!!tiktokEnabledWatch && (
                    <>
                      <FormUploadPreview
                        disabled={!hasEditPermission}
                        control={
                          <Input
                            multiple={false}
                            accept="image/png"
                            isInvalid={
                              !!errors?.tiktokLogoLink?.message ||
                              invalidPhoto ||
                              isErrorRatio
                            }
                            name="tiktokLogoLink"
                            disabled={!hasEditPermission}
                            onChange={async (
                              e: ChangeEvent<HTMLInputElement>,
                            ) => {
                              const file = e?.target?.files?.[0]
                              if (file) {
                                const data = await uploadImage(
                                  file,
                                  setIsUploading,
                                  setIsError,
                                  setShowMessageBox,
                                  setMessage,
                                  setIsErrorRatio,
                                  'NaN',
                                  false,
                                )
                                if (data.photoUrl) {
                                  setItems((i) => ({
                                    ...i,
                                    tiktokLink: data.photoUrl,
                                    tiktokName: file.name,
                                  }))
                                  setValue('tiktokLogoLink', data.photoKey, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  })
                                }
                              }
                            }}
                            type="file"
                          />
                        }
                        imageName={items.tiktokName || ''}
                        isUploading={isUploading}
                        url={items.tiktokLink || ''}
                        onRemove={() => {
                          setItems((i) => ({
                            ...i,
                            tiktokLink: '',
                            tiktokName: '',
                          }))
                          setValue('tiktokLogoLink', '')
                        }}
                        imageStyle={{
                          objectFit: 'contain',
                        }}
                        label="Tiktok Icon PNG"
                        previewLabel="Tiktok Icon PNG"
                      />
                      <FormGroup label="TikTok">
                        <Input
                          isInvalid={!!errors?.tiktokUrl?.message}
                          {...register('tiktokUrl')}
                          type="text"
                          placeholder="Enter URL"
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                    </>
                  )}
                </div>
                <div className="bg-monochrome-3-60 border border-monochrome-120 p-4 rounded-2xl space-y-5">
                  <FormGroup
                    label="Twitter"
                    direction="row"
                    labelClassName="whitespace-nowrap"
                    inputWrapperClassName="w-auto"
                    wrapperClassName="w-auto flex-row-reverse justify-end"
                    labelProps={{
                      htmlFor: 'twitterEnabled',
                    }}
                  >
                    <Checkbox
                      id="twitterEnabled"
                      checked={watch('twitterEnabled')}
                      onCheckedChange={(checked) => {
                        setValue('twitterEnabled', Boolean(checked))
                      }}
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  {!!twitterEnabledWatch && (
                    <>
                      <FormUploadPreview
                        disabled={!hasEditPermission}
                        control={
                          <Input
                            multiple={false}
                            accept="image/png"
                            isInvalid={
                              !!errors?.twitterLogoLink?.message ||
                              invalidPhoto ||
                              isErrorRatio
                            }
                            name="twitterLogoLink"
                            disabled={!hasEditPermission}
                            onChange={async (
                              e: ChangeEvent<HTMLInputElement>,
                            ) => {
                              const file = e?.target?.files?.[0]
                              if (file) {
                                const data = await uploadImage(
                                  file,
                                  setIsUploading,
                                  setIsError,
                                  setShowMessageBox,
                                  setMessage,
                                  setIsErrorRatio,
                                  'NaN',
                                  false,
                                )
                                if (data.photoUrl) {
                                  setItems((i) => ({
                                    ...i,
                                    twitterLink: data.photoUrl,
                                    twitterName: file.name,
                                  }))
                                  setValue('twitterLogoLink', data.photoKey, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  })
                                }
                              }
                            }}
                            type="file"
                          />
                        }
                        imageName={items.twitterName || ''}
                        isUploading={isUploading}
                        url={items.twitterLink || ''}
                        onRemove={() => {
                          setItems((i) => ({
                            ...i,
                            twitterLink: '',
                            twitterName: '',
                          }))
                          setValue('twitterLogoLink', '')
                        }}
                        imageStyle={{
                          objectFit: 'contain',
                        }}
                        label="Twitter Icon PNG"
                        previewLabel="Twitter Icon PNG"
                      />
                      <FormGroup label="Twitter">
                        <Input
                          isInvalid={!!errors?.twitterUrl?.message}
                          {...register('twitterUrl')}
                          type="text"
                          placeholder="Enter URL"
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                    </>
                  )}
                </div>
                <div className="bg-monochrome-3-60 border border-monochrome-120 p-4 rounded-2xl space-y-5">
                  <FormGroup
                    label="Telegram"
                    direction="row"
                    labelClassName="whitespace-nowrap"
                    inputWrapperClassName="w-auto"
                    wrapperClassName="w-auto flex-row-reverse justify-end"
                    labelProps={{
                      htmlFor: 'telegramEnabled',
                    }}
                  >
                    <Checkbox
                      id="telegramEnabled"
                      checked={watch('telegramEnabled')}
                      onCheckedChange={(checked) => {
                        setValue('telegramEnabled', Boolean(checked))
                      }}
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  {!!telegramEnabledWatch && (
                    <>
                      <FormUploadPreview
                        disabled={!hasEditPermission}
                        control={
                          <Input
                            multiple={false}
                            accept="image/png"
                            isInvalid={
                              !!errors?.telegramLogoLink?.message ||
                              invalidPhoto ||
                              isErrorRatio
                            }
                            name="telegramLogoLink"
                            disabled={!hasEditPermission}
                            onChange={async (
                              e: ChangeEvent<HTMLInputElement>,
                            ) => {
                              const file = e?.target?.files?.[0]
                              if (file) {
                                const data = await uploadImage(
                                  file,
                                  setIsUploading,
                                  setIsError,
                                  setShowMessageBox,
                                  setMessage,
                                  setIsErrorRatio,
                                  'NaN',
                                  false,
                                )
                                if (data.photoUrl) {
                                  setItems((i) => ({
                                    ...i,
                                    telegramLink: data.photoUrl,
                                    telegramName: file.name,
                                  }))
                                  setValue('telegramLogoLink', data.photoKey, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  })
                                }
                              }
                            }}
                            type="file"
                          />
                        }
                        imageName={items.telegramName || ''}
                        isUploading={isUploading}
                        url={items.telegramLink || ''}
                        onRemove={() => {
                          setItems((i) => ({
                            ...i,
                            telegramLink: '',
                            telegramName: '',
                          }))
                          setValue('telegramLogoLink', '')
                        }}
                        imageStyle={{
                          objectFit: 'contain',
                        }}
                        label="Telegram Icon PNG"
                        previewLabel="Telegram Icon PNG"
                      />
                      <FormGroup label="Telegram">
                        <Input
                          isInvalid={!!errors?.telegramUrl?.message}
                          {...register('telegramUrl')}
                          type="text"
                          placeholder="Enter URL"
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                    </>
                  )}
                </div>
                <div className="bg-monochrome-3-60 border border-monochrome-120 p-4 rounded-2xl space-y-5">
                  <FormGroup
                    label="Whatsapp"
                    direction="row"
                    labelClassName="whitespace-nowrap"
                    inputWrapperClassName="w-auto"
                    wrapperClassName="w-auto flex-row-reverse justify-end"
                    labelProps={{
                      htmlFor: 'whatsappEnabled',
                    }}
                  >
                    <Checkbox
                      id="whatsappEnabled"
                      checked={watch('whatsappEnabled')}
                      onCheckedChange={(checked) => {
                        setValue('whatsappEnabled', Boolean(checked))
                      }}
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  {!!whatsappEnabledWatch && (
                    <>
                      <FormUploadPreview
                        disabled={!hasEditPermission}
                        control={
                          <Input
                            multiple={false}
                            accept="image/png"
                            isInvalid={
                              !!errors?.whatsappLogoLink?.message ||
                              invalidPhoto ||
                              isErrorRatio
                            }
                            name="whatsappLogoLink"
                            disabled={!hasEditPermission}
                            onChange={async (
                              e: ChangeEvent<HTMLInputElement>,
                            ) => {
                              const file = e?.target?.files?.[0]
                              if (file) {
                                const { data } = await uploadNonPublicImage(
                                  file,
                                )
                                if (data.photoUrl) {
                                  setItems((i) => ({
                                    ...i,
                                    whatsappLink: data.photoUrl,
                                    whatsappName: file.name,
                                  }))
                                  setValue('whatsappLogoLink', data.photoKey, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  })
                                }
                              }
                            }}
                            type="file"
                          />
                        }
                        imageName={items.whatsappName || ''}
                        isUploading={isUploading}
                        url={items.whatsappLink || ''}
                        onRemove={() => {
                          setItems((i) => ({
                            ...i,
                            whatsappLink: '',
                            whatsappName: '',
                          }))
                          setValue('whatsappLogoLink', '')
                        }}
                        imageStyle={{
                          objectFit: 'contain',
                        }}
                        label="Whatsapp Icon PNG"
                        previewLabel="Whatsapp Icon PNG"
                      />
                      <FormGroup label="Whatsapp">
                        <Input
                          isInvalid={!!errors?.whatsappUrl?.message}
                          {...register('whatsappUrl')}
                          type="text"
                          placeholder="Enter URL"
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="bg-monochrome-3-60 border border-monochrome-120 p-4 rounded-2xl space-y-5">
            <FormGroup
              label="Description"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{
                htmlFor: 'descriptionEnabled',
              }}
            >
              <Checkbox
                id="descriptionEnabled"
                checked={watch('descriptionEnabled')}
                onCheckedChange={(checked) => {
                  setValue('descriptionEnabled', Boolean(checked))
                }}
                disabled={!hasEditPermission}
              />
            </FormGroup>
            {!!descWatch && (
              <div className="flex flex-col gap-3 w-full">
                {map(fieldsDescription, (field, index) => {
                  const content = watch(`descriptionTexts.${index}.text`)
                  return (
                    <Card
                      className="border border-monochrome-120 p-4 mb-3"
                      key={field.id}
                    >
                      <HtmlEditor
                        label={
                          <h5 className="text-sm font-semibold">
                            Language:{' '}
                            <span className="text-accent-1">
                              {
                                find(
                                  allLanguages,
                                  (cont) => field.locale === cont.locale,
                                )?.name
                              }
                            </span>
                          </h5>
                        }
                        control={control}
                        variables={variables}
                        hideCheckbox={true}
                        isError={
                          !!errors?.descriptionTexts?.[index]?.text?.message
                        }
                        name={`descriptionTexts.${index}.text`}
                        value={content || ''}
                        onChange={(data) => {
                          setValue(`descriptionTexts.${index}.text`, data)
                        }}
                        disabled={!hasEditPermission}
                        index={index}
                      />
                    </Card>
                  )
                })}
              </div>
            )}
          </div>
          <div className="bg-monochrome-3-60 border border-monochrome-120 p-4 rounded-2xl space-y-5">
            <FormGroup
              label="Copyright"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{
                htmlFor: 'copyrightEnabled',
              }}
            >
              <Checkbox
                id="copyrightEnabled"
                checked={watch('copyrightEnabled')}
                onCheckedChange={(checked) => {
                  setValue('copyrightEnabled', Boolean(checked))
                }}
                disabled={!hasEditPermission}
              />
            </FormGroup>
            {!!copyWatch && (
              <div className="flex flex-col gap-3 w-full">
                {map(fieldsCopyright, (field, index) => {
                  const content = watch(`copyrightTexts.${index}.text`)
                  return (
                    <Card
                      className="border border-monochrome-3-60 p-4 mb-3"
                      key={field.id}
                    >
                      <HtmlEditor
                        label={
                          <h5 className="text-sm font-semibold">
                            Language:{' '}
                            <span className="text-accent-1">
                              {
                                find(
                                  allLanguages,
                                  (cont) => field.locale === cont.locale,
                                )?.name
                              }
                            </span>
                          </h5>
                        }
                        control={control}
                        variables={variables}
                        hideCheckbox={true}
                        isError={
                          !!errors?.copyrightTexts?.[index]?.text?.message
                        }
                        name={`copyrightTexts.${index}.text`}
                        value={content || ''}
                        onChange={(data) => {
                          setValue(`copyrightTexts.${index}.text`, data)
                        }}
                        disabled={!hasEditPermission}
                        index={index}
                      />
                    </Card>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </form>

      <div className="flex justify-end gap-2 p-3 border-t border-monochrome-120 mt-4">
        <Button
          onClick={handleCancel}
          variant="secondary"
          data-dismiss="modal"
          disabled={!hasEditPermission}
          className="w-[154px]"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          form="settings-email-templates-footer-form"
          type="submit"
          disabled={!hasEditPermission}
          className="w-[154px]"
        >
          Save
        </Button>
      </div>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </Card>
  )
}
